import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { CheckboxField } from '@connected/form-fields/components/checkbox';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Button } from '@connected/ui/components/button';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Page, Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useTranslation } from 'react-i18next';

import { InformationLine, InformationLineText } from '@/components/information-line';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useCondition } from '@/config/conditions';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

export const Route = createFileRoute('/(auth)/projects/$projectVersionId/applications/recap/')({
    component: RecapPage,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.recapOptions({ electricalProjectVersionId: params.projectVersionId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }));
    },
});

function RecapPage() {
    const { t } = useTranslation('applications');

    return (
        <Page>
            <Container>
                <Container.Header>
                    <Container.Title>
                        {t('recap.layout.navigation.recap.label')}
                    </Container.Title>
                    <Container.SubTitle>
                        Object locatie
                    </Container.SubTitle>
                </Container.Header>
                <Container.Content>
                    <Information />
                </Container.Content>
                <Section.Footer>
                    <FooterNavigation />
                </Section.Footer>
            </Container>
        </Page>
    );
}

function Information() {
    const { t } = useTranslation('applications');

    const params = Route.useParams();

    const inspectionReports = useQuery(elektraAppQueryOptions.project.recapOptions({ electricalProjectVersionId: params.projectVersionId }));
    const { data, isLoading } = useQuery(elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }));

    const street = inspectionReports.data?.ProjectObject?.Street;
    const streetNumber = inspectionReports.data?.ProjectObject?.StreetNumber;

    let address: string | undefined;
    if (street && streetNumber) {
        address = `${street} ${streetNumber}`;
    }

    const postalCode = inspectionReports.data?.ProjectObject?.PostalCode;
    const locality = inspectionReports.data?.ProjectObject?.Locality;

    let postalCodeAndLocality: string | undefined;
    if (postalCode && locality) {
        postalCodeAndLocality = `${postalCode}, ${locality}`;
    }

    const totalFloorArea
        = inspectionReports.data?.ProjectObject?.TotalFloorArea;

    const notFound = t('recap.form.yet-unknown');

    if (isLoading) {
        return (
            <div className="mb-7">
                <Skeleton className="h-32" />
                <div className="flex flex-col items-center gap-2">
                    <Skeleton className="mt-6 h-7 w-40" />
                    <Skeleton className="h-5 w-20" />
                </div>
                <Skeleton className="mt-6 h-28" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <InformationLine
                    label={InformationLineText(
                        t('recap.form.streetName-houseNumbers'),
                    )}
                    value={address || notFound}
                />
                <InformationLine
                    label={InformationLineText(
                        t('recap.form.zipCode-location'),
                    )}
                    value={postalCodeAndLocality || notFound}
                />
                <InformationLine
                    label={InformationLineText(
                        t('recap.form.gross-square-meters'),
                    )}
                    value={totalFloorArea || notFound}
                />

                {data?.Organizations?.sort((a, b) => {
                    // Sort priority: IsProjectCustomer > IsObjectOwner > IsObjectAdministrator > IsObjectTenant
                    if (a.IsProjectCustomer !== b.IsProjectCustomer) {
                        return b.IsProjectCustomer ? 1 : -1;
                    }
                    if (a.IsObjectOwner !== b.IsObjectOwner) {
                        return b.IsObjectOwner ? 1 : -1;
                    }
                    if (a.IsObjectAdministrator !== b.IsObjectAdministrator) {
                        return b.IsObjectAdministrator ? 1 : -1;
                    }
                    if (a.IsObjectTenant !== b.IsObjectTenant) {
                        return b.IsObjectTenant ? 1 : -1;
                    }
                    return 0;
                })?.map((organization) => {
                    const organizationGroups = [
                        organization.IsObjectAdministrator
                            ? t(
                                    'recap.form.organization.is-object-administrator',
                                )
                            : '',
                        organization.IsObjectOwner
                            ? t(
                                    'recap.form.organization.is-object-owner',
                                )
                            : '',
                        organization.IsObjectTenant
                            ? t(
                                    'recap.form.organization.is-object-tenant',
                                )
                            : '',
                        organization.IsProjectCustomer
                            ? t(
                                    'recap.form.organization.is-project-customer',
                                )
                            : '',
                    ]
                        // remove the empty indexes
                        .filter(x => x !== undefined && x.length)
                        // join the array with comma's & "and" at the end
                        .reduce((carry, item, idx, arr) => {
                            if (carry === undefined) {
                                carry = '';
                            }

                            return idx === arr.length - 2
                                ? (carry += `${item} ${t('recap.layout.navigation.recap.and-label')} `)
                                : (carry += `${item}${idx === arr.length - 1 ? '' : ', '}`);
                        }, '');

                    return (
                        <Container key={organization.ElectricalProjectOrganizationId} className="border-none px-0">
                            <Container.Header>
                                <Container.Title>
                                    {organization?.Name}
                                </Container.Title>
                                <Container.SubTitle
                                    className={
                                        !organizationGroups?.length
                                            ? 'italic'
                                            : ''
                                    }
                                >
                                    {organizationGroups
                                        || t(
                                            'recap.form.organization.unknown-groups',
                                        )}
                                </Container.SubTitle>
                            </Container.Header>
                            <div>
                                {organization?.ContactPersons?.map(
                                    contactperson => (
                                        <Contactperson
                                            key={contactperson.ElectricalProjectContactPersonId}
                                            contactPerson={
                                                contactperson
                                            }
                                        />
                                    ),
                                )}
                                {!organization?.ContactPersons
                                    ?.length
                                    ? (
                                            <Container className="mb-4 gap-1 rounded-lg border-none bg-background-secondary px-4 pb-1 pt-3">
                                                <div className="mb-10 mt-7 flex justify-center italic">
                                                    {t(
                                                        'recap.form.organization.no-contact-persons',
                                                    )}
                                                </div>
                                            </Container>
                                        )
                                    : (
                                            ''
                                        )}
                            </div>
                        </Container>
                    );
                })}
            </div>
        </div>
    );
}

type ContactpersonValues = {
    receivePriceQuote: boolean;
    receiveInvoice: boolean;
    receiveInspectionPlan: boolean;
    receiveInspectionReport: boolean;
    receivePowerShutdownPlan: boolean;
};

function Contactperson({
    contactPerson,
}: {
    contactPerson: components['schemas']['ListElectricalProjectOrganizationQueryResultContact'];
}) {
    const { t } = useTranslation('applications');
    const params = Route.useParams();

    const isUserOffline = useOffline();

    // form for contactperson
    const { Field } = useForm<ContactpersonValues, ZodValidator>({
        defaultValues: {
            receivePriceQuote: contactPerson.ReceivePriceQuote || false,
            receiveInvoice: contactPerson.ReceiveInvoice || false,
            receiveInspectionPlan: contactPerson.ReceiveInspectionPlan || false,
            receiveInspectionReport:
                contactPerson.ReceiveInspectionReport || false,
            receivePowerShutdownPlan:
                contactPerson.ReceivePowerShutdownPlan || false,
        },
        validatorAdapter: zodValidator(),
    });

    // mutation

    // const patcher = createPatcher(
    //     '/api/v2/electrical/projects/contactpersons/patch',
    //     {
    //         ElectricalProjectContactPersonId:
    //             contactPerson.ElectricalProjectContactPersonId,
    //     },
    // );

    const { patchField } = usePatcher('/api/v2/electrical/projects/contactpersons/patch', {
        onSettled: () => {
            queryClient.invalidateQueries(
                elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
            );
        },
    }, {
        body: {
            ElectricalProjectContactPersonId: contactPerson.ElectricalProjectContactPersonId!,
        },
    });

    const canAccessQuotation = useCondition('canAccessQuotation');

    return (
        <Container className="mb-4 gap-1 rounded-lg border-none bg-background-secondary px-4 pb-1 pt-3">
            <div className="flex flex-col items-start">
                <span className="font-semibold">
                    {contactPerson.FirstName}
                    {' '}
                    {contactPerson?.MiddleName}
                    {' '}
                    {contactPerson.LastName}
                </span>
                <span
                    className={`text-text-tertiary ${!contactPerson.JobTitleDescription?.length
                        ? 'italic'
                        : ''
                    }`}
                >
                    {contactPerson.JobTitleDescription?.length
                        ? contactPerson.JobTitleDescription
                        : t(
                                'recap.form.contact-persons.unknown-job-title-description',
                            )}
                </span>
            </div>
            <div>
                <form className="flex flex-row items-start gap-2">
                    {[
                        ...(canAccessQuotation ? ['receivePriceQuote'] : []),
                        // 'receiveInvoice',
                        'receiveInspectionPlan',
                        'receiveInspectionReport',
                        // 'receivePowerShutdownPlan',
                    ].map(field => (
                        <Field
                            key={field}
                            name={field as keyof ContactpersonValues}
                            validators={{
                                onChange:
                                    validators.patchElectricalProjectContactPersonCommand[field as keyof ContactpersonValues],
                                onChangeAsync: patchField(field as keyof ContactpersonValues),
                                onBlurAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <div className="flex flex-col items-start">
                                    <CheckboxField
                                        name={name}
                                        value={state.value}
                                        label={t(
                                            `recap.form.contact-persons.${name}`,
                                        )}
                                        onChange={value => handleChange(value)}
                                        className="p-0"
                                        disabled={isUserOffline}
                                    />
                                    <FieldInfo state={state} />
                                </div>
                            )}
                        </Field>
                    ))}
                </form>
            </div>
        </Container>
    );
}

function Footer() {
    const { t } = useTranslation('common');

    const params = Route.useParams();
    const search = Route.useSearch();
    const canAccessQuotation = useCondition('canAccessQuotation');

    return (
        <footer className="flex justify-between gap-4">
            <Button variant="ghost" asChild>
                <Link
                    to="/projects/$projectVersionId/applications/drawings"
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.previous')}
                </Link>
            </Button>
            <Button asChild variant="ghost">
                <Link
                    to={canAccessQuotation ? '/projects/$projectVersionId/quotation' : '/projects/$projectVersionId/inspection-plans'}
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.next')}
                </Link>
            </Button>
        </footer>
    );
}
